import { faPenCircle } from "@fortawesome/pro-regular-svg-icons/faPenCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Stack, useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { PublicationType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContentPageAuthor } from "views/knowledge/components/card/behaviors/common/ContentPageAuthor";
import { FavoriteButton, FollowButton, ShareButton } from "views/knowledge/components/card/behaviors/common/Interaction";
import { PlaybackButton } from "views/knowledge/components/card/behaviors/list/defaults";
import { ContentOptions } from "views/knowledge/components/card/behaviors/list/MenuOptions";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { useCardManagerStyles } from "views/knowledge/components/card/CardManagerStyles";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { StickerBox } from "../common/StickerBox";

///
export const TitleContentPage = () => {
    const { pub: { title, sticker, expired, blocked, availableInTheFuture, viewPeriodStart, } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    const isBlocked = blocked?.blockedOn != undefined;

    return (
        <Box>
            <MprcFeedTypography variant="title" component="div" noWrap={true} color="inherit">
                <span title={title}>{title}</span>
            </MprcFeedTypography>
            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {expired && <StickerBox name={t("Feed.Card.Common.ExpiredText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {availableInTheFuture && <StickerBox name={t("Feed.Card.Common.DisplayOnText", { date: Formaters.dayMonth(viewPeriodStart) })} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {sticker && <StickerBox name={sticker.name} textColor={sticker.textColor} backgroundColor={sticker.backgroundColor} />}
            </Stack>
        </Box>
    );
};

///
export const ThumbnailContentPage = () => {
    const { pub: { listDefinition } } = useCardContext();

    const thumbnailUrl = listDefinition != null ? listDefinition.thumbnailUrl : AppImages.KnowledgeListThumbnail;

    return (
        <Box sx={{ width: 154, height: 87 }}>
            <img src={thumbnailUrl} width="100%" height="100%" style={{ objectFit: "contain", borderRadius: "6px" }} />
        </Box>
    );
};

///
export const AuthorListContentPage = () => {
    const { pub } = useCardContext();

    return pub.listDefinition?.isPublic === false
        ? <AuthorPrivateContentPage />
        : <ContentPageAuthor />;
};

///
const AuthorPrivateContentPage = () => {
    const { pub } = useCardContext();
    const { t } = useTranslation("knowledge");

    return (
        <Grid container justifyContent="center" alignItems="center" spacing={1}>
            <Grid size="grow">
                <MprcFeedTypography variant="authorPrimary" component="div">
                    <span title={t("Feed.Card.List.PrivateListText")}>{t("Feed.Card.List.PrivateListText")}</span>
                </MprcFeedTypography>

                <MprcFeedTypography variant="authorSecondary" component="div">
                    <span>{t("Feed.Card.Common.PublishOnText")} {" "} {Formaters.relativeTime(pub.publishedOn ?? pub.createdOn)}</span>
                </MprcFeedTypography>
            </Grid>
        </Grid>
    );
};

///
export const FooterContentPage = () => {
    const { pub } = useCardContext();

    return (
        pub.listDefinition?.isPublic === true
            ? <FooterPublicContentPage />
            : <FooterPrivateContentPage />
    );
};

///
export const IconActionsContentPage = () => {
    const { pub: { publicationId, publicationType, interaction: { allowEdit } } } = useCardContext();
    const [dialog, setDialog] = useState<React.ReactNode | null>(null);
    const { classes } = useCardManagerStyles();

    const doEdit = useCallback(() => {
        switch (publicationType) {
            case PublicationType.List: {
                setDialog(publicationFactory(publicationId, publicationType, handleClose, { isPublic: true }));
                break;
            }
        }
    }, []);

    ///
    function handleClose() {
        setDialog(null);
    }

    return (
        <>
            {dialog}

            <div style={{ height: 24 }} className={classes.cardMouseOut}>
                {
                    allowEdit &&
                    <IconButton color="default" size="small" onClick={doEdit}>
                        <FontAwesomeIcon icon={faPenCircle} fixedWidth />
                    </IconButton>
                }
            </div>
        </>
    );
};

///
export const FooterPublicContentPage = () => {

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={1} justifyContent="center" alignItems="center">
                    <Grid>
                        <PlaybackButton />
                    </Grid>
                    <Grid>
                        <FollowButton />
                    </Grid>
                </Grid>
            </Grid>
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <ContentOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

///
export const FooterPrivateContentPage = () => {
    return (
        <Grid container justifyContent="end" alignItems="center">
            <Grid >
                <ContentOptions />
            </Grid>
        </Grid>
    );
};
